import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../components/QualityKpi/QualityKpi.css';
import FiltersSidebar from '../components/QualityKpi/FiltersSidebar';
import ProductionIncidentTab from '../components/QualityKpi/ProductionIncidentTab';
import AutomationCoverageTab from '../components/QualityKpi/AutomationCoverageTab';
import DefectSeverityIndexTab from '../components/QualityKpi/DefectSeverityIndexTab';
import QualityOverviewTab from '../components/QualityKpi/QualityOverviewTab';
import { computeRangeForPreset, PRESETS } from '../components/Filters/DateRangePicker';

const TABS = [
  { key: 'quality-overview', label: 'Quality Overview', Component: QualityOverviewTab },
  { key: 'production-incident', label: 'Production Incident', Component: ProductionIncidentTab },
  { key: 'automation-coverage', label: 'Automation Coverage', Component: AutomationCoverageTab },
  { key: 'defect-severity-index', label: 'Defect Severity Index', Component: DefectSeverityIndexTab },
];

const DEFAULT_TAB = 'quality-overview';
const FILTER_KEYS = ['domain', 'group', 'unit'];

// Date range is shared via the resolved `from`/`to` dates, not the preset
// name — a preset like "30d" is relative to "today", so encoding only the
// preset would make a shared link show a different range depending on when
// it's opened. `from`/`to` make the link reproduce the exact same report.
const formatDateParam = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseDateParam = (s) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  date.setHours(0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Figures out which preset (if any) produced this exact range, so the right
// toggle button is highlighted when a link is opened — falls back to
// "custom" if it doesn't match one of the fixed presets.
function detectPreset(startDate, endDate) {
  const target = `${formatDateParam(startDate)}_${formatDateParam(endDate)}`;
  const match = PRESETS.filter(p => p !== 'custom').find(p => {
    const r = computeRangeForPreset(p);
    return `${formatDateParam(r.startDate)}_${formatDateParam(r.endDate)}` === target;
  });
  return match || 'custom';
}

function readFiltersFromParams(searchParams) {
  const filters = {};
  FILTER_KEYS.forEach(key => { filters[key] = searchParams.getAll(key); });
  return filters;
}

function readDateRangeFromParams(searchParams) {
  const startDate = parseDateParam(searchParams.get('from'));
  const endDate = parseDateParam(searchParams.get('to'));
  if (startDate && endDate) {
    return { startDate, endDate, preset: detectPreset(startDate, endDate) };
  }
  return { ...computeRangeForPreset('30d'), preset: '30d' };
}

function QualityKpi() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || DEFAULT_TAB);
  const [filters, setFilters] = useState(() => readFiltersFromParams(searchParams));
  const [dateRange, setDateRange] = useState(() => readDateRangeFromParams(searchParams));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Single source of truth for the shareable URL — any filter, date range,
  // or tab change updates it, mirroring the pattern in Campaigns.js.
  useEffect(() => {
    const params = new URLSearchParams();
    FILTER_KEYS.forEach(key => { filters[key].forEach(v => params.append(key, v)); });
    if (dateRange.startDate && dateRange.endDate) {
      params.set('from', formatDateParam(dateRange.startDate));
      params.set('to', formatDateParam(dateRange.endDate));
    }
    if (activeTab && activeTab !== DEFAULT_TAB) params.set('tab', activeTab);
    setSearchParams(params, { replace: true });
  }, [filters, dateRange, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const active = TABS.find(t => t.key === activeTab) ?? TABS[0];
  const ActiveComponent = active.Component;

  return (
    <div className="application-container qk-page">
      <div className="qk-header">
        <h1>📊 Quality KPI</h1>
      </div>

      <div className="qk-body">
        <div className="qk-sidebar-slot">
          <FiltersSidebar
            filters={filters}
            onFiltersChange={setFilters}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            hideDateRange={activeTab === 'automation-coverage'}
            collapsed={sidebarCollapsed}
            onToggleCollapsed={() => setSidebarCollapsed(c => !c)}
          />
        </div>

        <div className="qk-main">
          <div className="qk-tabs">
            {TABS.map(t => (
              <button
                key={t.key}
                className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="qk-tab-panel">
            <ActiveComponent filters={filters} dateRange={dateRange} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default QualityKpi;
